/**
 * Mirrors server/src/lib/zoomEmails.ts spotlight helpers (server is source of truth for assembly).
 * Used on Build → Zoom Emails so preview / test / queue include the optional message + graphic.
 */

export const ZOOM_SPOTLIGHT_SLOT = '<!-- emailcraft-zoom-spotlight-slot -->';
const ZOOM_SPOTLIGHT_OPEN = '<!-- emailcraft-zoom-spotlight -->';
const ZOOM_SPOTLIGHT_CLOSE = '<!-- /emailcraft-zoom-spotlight -->';

export interface ZoomSpotlightContent {
  message?: string;
  /** Public http(s) image URL (e.g. Interspire Image Manager). */
  imageUrl?: string;
  /** Optional click-through; only http(s) are kept. */
  linkUrl?: string;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Allow only http/https absolute URLs for email links and image src. */
export function sanitizeSpotlightLinkUrl(raw: string): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  try {
    const u = new URL(s);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    return u.toString();
  } catch {
    return '';
  }
}

/** True when the Zoom template left a {{ZOOM_SPOTLIGHT}} slot (preferred insert point). */
export function htmlHasZoomSpotlightSlot(html: string): boolean {
  const s = String(html ?? '');
  return s.includes(ZOOM_SPOTLIGHT_SLOT) || /\{\{ZOOM_SPOTLIGHT\}\}/.test(s);
}

/** Mirrors server zoomSectionTopRuleHtml — Outlook-friendly top rule only. */
export function zoomSectionTopRuleHtml(): string {
  return [
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:16px 0 0 0;border-collapse:collapse;border-spacing:0;">`,
    `<tr><td style="border-top:1px solid #cccccc;font-size:0;line-height:0;height:1px;mso-line-height-rule:exactly;">&nbsp;</td></tr>`,
    `<tr><td style="font-size:0;line-height:0;height:15px;mso-line-height-rule:exactly;">&nbsp;</td></tr>`,
    `</table>`,
  ].join('');
}

export function buildZoomSpotlightHtml(content: ZoomSpotlightContent): string {
  const message = String(content.message ?? '').trim();
  const safeImageSrc = sanitizeSpotlightLinkUrl(content.imageUrl ?? '');
  const safeHref = sanitizeSpotlightLinkUrl(content.linkUrl ?? '');
  if (!message && !safeImageSrc) return '';

  const parts: string[] = [];
  if (message) {
    const paras = message
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map(
        (p) =>
          `<p style="margin:0 0 12px 0;padding:0;font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.45;color:#333333;text-align:center;">${escapeHtml(p).replace(/\n/g, '<br>')}</p>`
      );
    parts.push(...paras);
  }
  if (safeImageSrc) {
    const img = `<img src="${escapeHtml(safeImageSrc)}" alt="" width="600" style="display:block;margin:0 auto;max-width:100%;height:auto;border:0;outline:none;text-decoration:none;" />`;
    parts.push(
      safeHref
        ? `<a href="${escapeHtml(safeHref)}" target="_blank" style="text-decoration:none;border:0;outline:none;">${img}</a>`
        : img
    );
  } else if (message && safeHref) {
    const wrapped = parts.join('\n');
    parts.length = 0;
    parts.push(
      `<a href="${escapeHtml(safeHref)}" target="_blank" style="text-decoration:none;color:#0563C1;">${wrapped}</a>`
    );
  }

  return [
    ZOOM_SPOTLIGHT_OPEN,
    zoomSectionTopRuleHtml(),
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 16px 0;border-collapse:collapse;">`,
    `<tr><td style="padding:16px 8px;text-align:center;">${parts.join('\n')}</td></tr>`,
    `</table>`,
    ZOOM_SPOTLIGHT_CLOSE,
  ].join('');
}

/** Index before the block that contains "Grant Writing Workshops" (not mid-tag). */
function findWorkshopsInsertIndex(html: string): number | null {
  const heading = /Grant\s*Writing\s*Workshops/i.exec(html);
  if (!heading || heading.index == null) return null;
  const end = heading.index;
  const windowStart = Math.max(0, end - 400);
  const slice = html.slice(windowStart, end);
  const tagRe = /<(p|h[1-6]|div|td|th|li)(\s[^>]*)?>/gi;
  let last = -1;
  let m: RegExpExecArray | null;
  while ((m = tagRe.exec(slice)) !== null) {
    last = windowStart + m.index;
  }
  return last >= 0 ? last : end;
}

/** Insert or remove spotlight: slot / {{ZOOM_SPOTLIGHT}} / before "Grant Writing Workshops". */
export function applyZoomSpotlight(html: string, content: ZoomSpotlightContent | null): string {
  let out = String(html ?? '');
  out = out.replace(
    new RegExp(
      `${escapeRegExp(ZOOM_SPOTLIGHT_OPEN)}[\\s\\S]*?${escapeRegExp(ZOOM_SPOTLIGHT_CLOSE)}`,
      'g'
    ),
    ''
  );

  const block = content ? buildZoomSpotlightHtml(content) : '';
  if (!block) {
    return out.replace(new RegExp(escapeRegExp(ZOOM_SPOTLIGHT_SLOT), 'g'), '').trim();
  }

  if (out.includes(ZOOM_SPOTLIGHT_SLOT)) {
    return out.replace(new RegExp(escapeRegExp(ZOOM_SPOTLIGHT_SLOT), 'g'), block);
  }
  if (/\{\{ZOOM_SPOTLIGHT\}\}/.test(out)) {
    return out.replace(/\{\{ZOOM_SPOTLIGHT\}\}/g, block);
  }

  const insertAt = findWorkshopsInsertIndex(out);
  if (insertAt != null) {
    return out.slice(0, insertAt) + block + out.slice(insertAt);
  }

  const bodyClose = /<\/body>/i.exec(out);
  if (bodyClose && bodyClose.index != null) {
    return out.slice(0, bodyClose.index) + block + out.slice(bodyClose.index);
  }
  return `${out}\n${block}`;
}
